import React, { useState } from 'react';
import { Button, Divider } from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PatientCasesList from '@brands/Dashboard/ConsultationPage/Components/PatientCasesList/PatientCasesList';

import { FALLBACK_AVATAR } from '../utils/consultationHelpers';
import styles from '../styles.module.scss';
import { ICase } from '@brands/services/cases/types/ICase';
import { Insurance, UserProfile, UserRoleName } from '@brands/services/identity/types/UserProfile';
import { DentalHistory, MedicalConditions, MedicalHistory } from '@brands/services/clinical_data/types/types';
import { getUserAvatar } from '@brands/services/identity/getUserAvatar';
import { getGenderName } from '@brands/Utils/getGenderName';
import { pronounsOptions } from '@brands/Utils/selectButtonOptions';
import PatientProfileCardModal from '../../ProfileCards/Patient/ProfileCards';

type Props = {
  thisCase: ICase;
  userInfo: UserProfile;
  medicalCondition: MedicalConditions | null;
  medicalHistory: MedicalHistory | null;
  dentalHistory: DentalHistory | null;
  pharmacyAddress: string;
  pharmacyPhoneNumber: string;
  displayPhoneNumber: string;
  secondaryInsurance?: Insurance;
  handleOpenInsuranceCard: (insurance?: Insurance) => Promise<void>;
  redirectToConversationPage: (myRoleName: string, withRoleName: string) => void;
  onCollectorMessageClick?: (withRole: string) => void;
};

const sectionDividerSx = {
  my: 1.5,
  width: '100%',
  minWidth: '100%',
  height: '1px',
  border: 'none',
  backgroundColor: 'rgba(0, 0, 0, 0.12)',
  opacity: 1,
  display: 'block',
  flexShrink: 0,
} as const;

const PatientInfoPanel = ({
  thisCase,
  userInfo,
  medicalCondition,
  medicalHistory,
  dentalHistory,
  pharmacyAddress,
  pharmacyPhoneNumber,
  displayPhoneNumber,
  secondaryInsurance,
  handleOpenInsuranceCard,
  redirectToConversationPage,
  onCollectorMessageClick,
}: Props): JSX.Element => {
  const isCollector = userInfo.role.name === UserRoleName.Collector;
  const canSeeInsurance = userInfo.role.name !== UserRoleName.Provider && userInfo.role.name !== UserRoleName.Admin;
  const [profileCardModal, setProfileCardModal] = useState(false);
  const [profileCardData, setProfileCardData] = useState<any>([]);

  // Older cases (and flows that haven't been migrated to record which insurance
  // actually passed verification) only ever populate `insurance`, not `used_insurance`.
  const displayInsurance = thisCase.used_insurance ?? thisCase.insurance;

  // A non-insurance case is only "Self pay" when it actually carries a cost.
  // Free/report-only configurations (amount 0, no payment options) must read
  // "Free case" rather than implying the patient was charged.
  const paymentMethodLabel = ((): string => {
    if (thisCase.paid_with_credits) return 'Plan credits';
    if (thisCase.plan_discount_applied_percent && thisCase.plan_discount_applied_percent > 0) {
      return `Plan discount (${thisCase.plan_discount_applied_percent}%)`;
    }
    if (thisCase.pricing?.amount && thisCase.pricing.amount > 0) {
      return displayInsurance ? 'Insurance' : 'Self pay';
    }
    return 'Free case';
  })();

  return (
    <>
      <div className={styles.consultationPageLeftSideBlock}>
        <span className={styles.consultationPageLeftSideBlockSpan}>Patient details</span>
        <div className={styles.consultationPageLeftSideBlockContent}>
          <div className={styles.consultationPageLeftSideBlockContentFram}>
            <div className={styles.consultationPageLeftSideBlockContentFramCATeamInfo}>
              <img
                alt="Avatar"
                className={`fs-exclude ${styles.consultationPageLeftSideBlockContentFramCATeamInfoImage}`}
                src={getUserAvatar(Number(thisCase.patient.id))}
                role="button"
                tabIndex={0}
                style={{ cursor: 'pointer' }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setProfileCardData(thisCase.patient);
                    setProfileCardModal(true);
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setProfileCardData(thisCase.patient);
                  setProfileCardModal(true);
                }}
                onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
                  const img = event.currentTarget;
                  img.src = FALLBACK_AVATAR;
                  img.onerror = null;
                }}
              />

              <div className={styles.consultationPageLeftSideBlockContentFramCATeamInfoNameDiv}>
                <div
                  className={styles.consultationPageLeftSideBlockContentFramCATeamInfoName}
                  role="button"
                  tabIndex={0}
                  style={{ cursor: 'pointer' }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setProfileCardData(thisCase.patient);
                      setProfileCardModal(true);
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setProfileCardData(thisCase.patient);
                    setProfileCardModal(true);
                  }}
                >
                  <span className={styles.consultationPageLeftSideBlockContentFramCATeamInfoNameTitle}>Patient</span>
                  <span className={styles.consultationPageLeftSideBlockContentFramCATeamInfoNameValue}>
                    {thisCase.patient.full_name} {thisCase.patient.status === 'Deactivated' && '(Deactivated)'}
                  </span>
                </div>
                {profileCardModal && (
                  <PatientProfileCardModal
                    isOpen={profileCardModal}
                    toggleModal={setProfileCardModal}
                    patientIdentity={profileCardData}
                  />
                )}

                <div
                  className={styles.consultationPageLeftSideBlockContentFramCATeamInfoMessage}
                  onClick={() =>
                    isCollector
                      ? onCollectorMessageClick?.(UserRoleName.Patient)
                      : redirectToConversationPage(userInfo.role.name, UserRoleName.Patient)
                  }
                >
                  <span>
                    <MailOutlineIcon />
                  </span>
                </div>
              </div>
            </div>

            <div className={styles.consultationPageLeftSideBlockContentFramCATeamDetails}>
              <span>
                {getGenderName(thisCase.patient?.basic_info?.gender)}
                <Divider orientation="vertical" flexItem className={styles.divider} />
                {pronounsOptions.find((option) => option.value === thisCase.patient?.basic_info?.pronouns)?.label ||
                  thisCase.patient?.basic_info?.pronouns}
              </span>
              <span>
                Language(s):{' '}
                {thisCase.patient?.basic_info?.languages
                  ?.map((language) => (typeof language !== 'string' ? language.name : ''))
                  .join(', ')}
              </span>
            </div>
            <div className={styles.consultationPageLeftSideBlockContentFramCATeamDetails}>
              <span className={styles.consultationPageLeftSideBlockContentFramCATeamDetailsTitle}>Address</span>
              <span style={{ maxWidth: '164px', height: 'fit-content' }}>
                {[
                  thisCase.patient?.address?.address1,
                  thisCase.patient?.address?.address2,
                  thisCase.patient?.address?.city,
                  thisCase.patient?.address?.state,
                  thisCase.patient?.address?.zip_code,
                ]
                  .filter(Boolean)
                  .join(', ')}
              </span>
            </div>

            <div className={styles.consultationPageLeftSideBlockContentFramCATeamDetails}>
              <span className={styles.consultationPageLeftSideBlockContentFramCATeamDetailsTitle}>Contact info</span>
              <a
                href={`tel:${displayPhoneNumber?.replace(/\s+/g, '')}`}
                className={styles.consultationPageLeftSideBlockContentFramCATeamDetailsTitlePhoneNumber}
              >
                {displayPhoneNumber}
              </a>
              <span>{thisCase.patient?.email || thisCase.patient?.guardian?.email}</span>
            </div>
          </div>
        </div>
      </div>
      {canSeeInsurance ? (
        <div className={styles.consultationPageLeftSideBlock}>
          <span className={styles.consultationPageLeftSideBlockSpan}>Payment and insurance</span>
          <div className={styles.consultationPageLeftSideBlockContent}>
            <div className={styles.consultationPageLeftSideBlockContentFram}>
              <div className={styles.consultationPageLeftSideBlockContentFramCATeamDetails}>
                <span className={styles.consultationPageLeftSideBlockContentFramCATeamDetailsTitle}>Payment method</span>
                <span>{paymentMethodLabel}</span>
              </div>

              {displayInsurance ? (
                <div
                  className={`${styles.consultationPageLeftSideBlockContentFramCATeamDetails} ${styles.consultationPageLeftSideBlockContentFramRowDetails}`}
                >
                  <div>
                    <span className={styles.consultationPageLeftSideBlockContentFramCATeamDetailsTitle}>
                      Insurance company
                    </span>
                    <span>{displayInsurance.insurance_membership?.insurance_carrier?.name}</span>
                  </div>
                  <div>
                    <span className={styles.consultationPageLeftSideBlockContentFramCATeamDetailsTitle}>Member ID</span>
                    <span>{displayInsurance.insurance_membership?.member_id}</span>
                  </div>
                </div>
              ) : null}
            </div>

            {displayInsurance?.media?.length ? (
              <Button
                variant="text"
                className={styles.insuranceViewCardBtn}
                onClick={() => void handleOpenInsuranceCard(displayInsurance)}
              >
                View insurance card
              </Button>
            ) : null}
          </div>
        </div>
      ) : null
      }
      {secondaryInsurance && canSeeInsurance ? (
        <div className={styles.consultationPageLeftSideBlock}>
          <span className={styles.consultationPageLeftSideBlockSpan}>Secondary insurance</span>
          <div className={styles.consultationPageLeftSideBlockContent}>
            <div className={styles.consultationPageLeftSideBlockContentFram}>
              <div
                className={`${styles.consultationPageLeftSideBlockContentFramCATeamDetails} ${styles.consultationPageLeftSideBlockContentFramRowDetails}`}
              >
                <div>
                  <span className={styles.consultationPageLeftSideBlockContentFramCATeamDetailsTitle}>
                    Insurance company
                  </span>
                  <span>{secondaryInsurance.insurance_membership?.insurance_carrier?.name}</span>
                </div>
                <div>
                  <span className={styles.consultationPageLeftSideBlockContentFramCATeamDetailsTitle}>Member ID</span>
                  <span>{secondaryInsurance.insurance_membership?.member_id}</span>
                </div>
              </div>
            </div>

            {secondaryInsurance.media?.length ? (
              <Button
                variant="text"
                className={styles.insuranceViewCardBtn}
                onClick={() => void handleOpenInsuranceCard(secondaryInsurance)}
              >
                View insurance card
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
      {userInfo.role.name !== UserRoleName.Admin ? (
        <>
          <div className={styles.consultationPageLeftSideBlock}>
            <span className={styles.consultationPageLeftSideBlockSpan}>Medical and dental information</span>
            <div className={styles.consultationPageLeftSideBlockContent}>
              <div className={styles.consultationPageLeftSideBlockContentFram}>
                {medicalHistory?.medications_allergies && (
                  <div className={styles.consultationPageLeftSideBlockContentFramCATeamDetails}>
                    <span className={styles.consultationPageLeftSideBlockContentFramCATeamDetailsTitle}>Allergies</span>
                    <span>{medicalHistory?.medications_allergies}</span>
                  </div>
                )}
                {medicalHistory?.conditions_treated && (
                  <div className={styles.consultationPageLeftSideBlockContentFramCATeamDetails}>
                    <span className={styles.consultationPageLeftSideBlockContentFramCATeamDetailsTitle}>
                      Treated conditions
                    </span>
                    <span>{medicalHistory?.conditions_treated}</span>
                  </div>
                )}
                {medicalHistory?.medications && (
                  <div className={styles.consultationPageLeftSideBlockContentFramCATeamDetails}>
                    <span className={styles.consultationPageLeftSideBlockContentFramCATeamDetailsTitle}>
                      Medications
                    </span>
                    <span>{medicalHistory?.medications}</span>
                  </div>
                )}
                <div
                  className={`${styles.consultationPageLeftSideBlockContentFramCATeamDetails} ${styles.consultationPageLeftSideBlockContentFramRowDetails}`}
                >
                  <div>
                    <span className={styles.consultationPageLeftSideBlockContentFramCATeamDetailsTitle}>Height</span>
                    <span>{medicalHistory?.height}</span>
                  </div>
                  <div>
                    <span className={styles.consultationPageLeftSideBlockContentFramCATeamDetailsTitle}>Weight</span>
                    <span>{medicalHistory?.weight}</span>
                  </div>
                </div>
                <Divider component="div" sx={sectionDividerSx} />
                <div className={styles.consultationPageLeftSideBlockContentFramCATeamDetails}>
                  <span className={styles.consultationPageLeftSideBlockContentFramCATeamDetailsTitle}>
                    Medical conditions
                  </span>
                  <span>
                    {medicalCondition
                      ? Object.entries(medicalCondition)
                          .filter(([key]) => !['id', 'user_id', 'created_at', 'updated_at'].includes(key))
                          .map(([key, value]) =>
                            value
                              ? key === 'conditions_not_listed'
                                ? value
                                : key === 'none_of_the_above'
                                ? 'None of the above'
                                : key.replace(/_/g, ' ')
                              : null,
                          )
                          .filter(Boolean)
                          .join(', ')
                      : ''}
                  </span>
                </div>
                {medicalHistory?.using_substances && medicalHistory.using_substances.length > 0 && (
                  <>
                    <Divider component="div" sx={sectionDividerSx} />
                    <div className={styles.consultationPageLeftSideBlockContentFramCATeamDetails}>
                      <span className={styles.consultationPageLeftSideBlockContentFramCATeamDetailsTitle}>I use</span>
                      <span>{medicalHistory.using_substances.join(', ')}</span>
                    </div>
                    <div className={styles.consultationPageLeftSideBlockContentFramCATeamDetails}>
                      <span className={styles.consultationPageLeftSideBlockContentFramCATeamDetailsTitle}>
                        Am I interested in stopping?
                      </span>
                      <span>
                        {medicalHistory.interested_in_stopping == null
                          ? ''
                          : medicalHistory.interested_in_stopping
                          ? 'Yes'
                          : 'No'}
                      </span>
                    </div>
                  </>
                )}
              </div>
              <div className={styles.consultationPageLeftSideBlockContentFram}>
                <div
                  className={`${styles.consultationPageLeftSideBlockContentFramCATeamDetails} ${styles.consultationPageLeftSideBlockContentFramRowDetails}`}
                >
                  <div>
                    <span className={styles.consultationPageLeftSideBlockContentFramCATeamDetailsTitle}>
                      Date of last dental visit
                    </span>
                    <span>
                      {dentalHistory?.last_dental_visit === 'dontKnow'
                        ? 'Not sure'
                        : dentalHistory?.last_dental_visit
                        ? new Date(dentalHistory.last_dental_visit).toLocaleDateString('en-US', {
                            month: 'long',
                            year: 'numeric',
                          })
                        : ''}
                    </span>
                  </div>

                  <div>
                    <span className={styles.consultationPageLeftSideBlockContentFramCATeamDetailsTitle}>
                      Visit type
                    </span>
                    <span>{dentalHistory?.visit_type}</span>
                  </div>
                </div>

                <div className={styles.consultationPageLeftSideBlockContentFramCATeamDetails}>
                  <span className={styles.consultationPageLeftSideBlockContentFramCATeamDetailsTitle}>
                    Who did you see?
                  </span>
                  <span>
                    {dentalHistory?.who_did_you_see === 'dontKnow' ? 'Not sure' : dentalHistory?.who_did_you_see || ''}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}

      <div className={styles.consultationPageLeftSideBlock}>
        <span className={styles.consultationPageLeftSideBlockSpan}>Pharmacy</span>
        <div className={styles.consultationPageLeftSideBlockContent}>
          <div className={styles.consultationPageLeftSideBlockContentFram}>
            <div className={styles.consultationPageLeftSideBlockContentFramCATeamDetails}>
              <span className={styles.consultationPageLeftSideBlockContentFramCATeamDetailsTitle}>Pharmacy</span>
              <span>{medicalHistory?.pharmacy_name}</span>
            </div>
            <div className={styles.consultationPageLeftSideBlockContentFramCATeamDetails}>
              <span className={styles.consultationPageLeftSideBlockContentFramCATeamDetailsTitle}>Location</span>
              <span>{pharmacyAddress}</span>
            </div>
            <div className={styles.consultationPageLeftSideBlockContentFramCATeamDetails}>
              <span className={styles.consultationPageLeftSideBlockContentFramCATeamDetailsTitle}>Phone number</span>
              <span>{pharmacyPhoneNumber}</span>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.consultationPageLeftSideBlock}>
        <span className={styles.consultationPageLeftSideBlockSpan}>Client tags</span>
        <div className={styles.consultationPageLeftSideBlockContent}>
          <div className={styles.consultationPageLeftSideBlockContentFram}>
            <div className={styles.consultationPageLeftSideBlockContentFramCATeamDetails}>
              <span>{thisCase.client?.name || '-'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.consultationPageLeftSideBlock}>
        <span className={styles.consultationPageLeftSideBlockSpan}>Patient case history</span>
        <PatientCasesList
          patient_id={thisCase.patient_id}
          currentCaseId={thisCase.id}
          patient_name={thisCase.patient.full_name}
        />
      </div>
    </>
  );
};

export default PatientInfoPanel;
